import { createRef, h } from 'preact';
import { BaseAnnotation } from '../base_annotation';
import { accessible , accessibleMenuButtons} from 'utilities/accessibility';
import { FaArrowUp, FaArrowDown, FaCheck} from 'react-icons/fa';
import elementClosestPolyfill from 'element-closest';
import './controlsMenu.scss';
import { LevelCheckMarkIcon } from './icons/LevelCheckMarkIcon';
import { APPLICATION_EVENT } from 'constants/events';

elementClosestPolyfill(window);

const MENU_ITEM_HEIGHT = 30;
const DEFAULT_OFFSET = '-1000px';

export class ControlsMenu extends BaseAnnotation {
    constructor(props) {
        super(props);

        this._timeout = null;
        this.menuRef = createRef();
        this.menuButtonRef = createRef();
        this.firstMenuItemRef = createRef();
        this.handleClickOutsideMenu = this.handleClickOutsideMenu.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutsideMenu);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutsideMenu);
    }

    handleClickOutsideMenu = e => {
        if (!this.menuIsActive) return;

        const menuButtonClicked = this.menuButtonRef.current.contains(e.target);
        const menuClicked = this.menuRef?.current?.contains?.(e.target);

        if (!menuClicked && !menuButtonClicked) {
            this.sendToggleEvent();
        }
    }

    postScroll = () => {
        this._timeout = setTimeout(() => {
            this._timeout = null;
            this.setState({
                scrolling: false,
            });
        }, 200);
    };

    handleScroll(event) {
        if (this._timeout) {
            clearTimeout(this._timeout);
        }

        this.setState({scrolling: true }, this.postScroll);
    }

    sendToggleEvent = () => {
        this.props?.toggleMenuOpen?.();
    }

    toggleMenu = (event) => {
        this.eventBus.emit(APPLICATION_EVENT.LEVEL_MENU_OPENED);
        this.allowOverflow(event.currentTarget, true);
        this.sendToggleEvent();
    };

    getOptionIcon = (isActive, level) => {
        const iconStyle = { opacity: isActive ? 1 : 0 };
        const iconAriaLabels = isActive ? 'selected' : 'not selected';
        return (
           <LevelCheckMarkIcon level={level} title={iconAriaLabels} style={iconStyle}/>
        );
    };

    get options() {
        const list = this.menuItems.map((option, index) => {
            const formattedOption = this.formatOption(option);
            const isActive = option === this.activeOption;
            const icon = this.getOptionIcon(isActive, formattedOption);
            return (
                <li
                    ref={index === 0 ? this.firstMenuItemRef : null}
                    style={{height: `${MENU_ITEM_HEIGHT}px`, width: '100%'}}
                    className={isActive ? 'active' : ''}
                    aria-label={isActive ? `selected ${formattedOption}` : `not selected ${formattedOption}`}
                    onClick={this.changeOption.bind(this, option)}
                    {...accessible(this.changeOption.bind(this, option), "menuitem")}
                    {...accessibleMenuButtons(this.menuButtonRef)}
                >
                    {icon}{formattedOption}
                </li>
            );
        });

        return <ul role="menu">{list}</ul>;
    }

    get definedMaxHeight() {
        const { dimensions = {} } = this.props;
        if (!dimensions.container) return 0;

        const topMargin = 64;
        return dimensions.container.height - topMargin;
    }

    get totalItemsHeight () {
        return this.menuItems.length * MENU_ITEM_HEIGHT;
    }

    get hasOverflow() {
        return this.totalItemsHeight > this.definedMaxHeight;
    }

    get styles() {
        const styles = {
            bottom: '24px',
            display: 'none',
            right: DEFAULT_OFFSET,
        }

        if (this.menuIsActive) {
            styles.right = '2px';
            styles.display = 'block';
        }

        return styles;
    }

    get scrollToValues() {
        return this.menuItems.map((item, idx) => idx * MENU_ITEM_HEIGHT);
    }

    get currentTop() {
        const { current: ref } = this.menuRef;
        return ref && ref.scrollTop;
    }

    findScrollTo = direction => {
        const currentOffset = this.currentTop;
        const scrollToValues = this.scrollToValues;
        const next = val => currentOffset < val;
        const prev = () => scrollToValues[scrollToValues.indexOf(scrollToValues.find(next)) - 2];
        return direction === 'next' ? scrollToValues.find(next) : prev();
    };

    scroll = (e, direction) => {
        e.stopPropagation();

        const { current: ref } = this.menuRef;
        if (!ref) return;

        const nextValue = this.findScrollTo(direction);

        // Scroll to zero if less than first item's height
        ref.scrollTo(0, nextValue <= MENU_ITEM_HEIGHT && direction === 'prev' ? 0 : nextValue);
    };

    scrollDown = e => this.scroll(e, 'next');

    scrollUp = e => this.scroll(e, 'prev');

    get canScrollUp() {
        return this.currentTop >= this.scrollToValues[1];
    }

    get canScrollDown() {
        const { current: menu } = this.menuRef;
        if (!menu) return false;
        return menu.scrollHeight - menu.scrollTop !== menu.clientHeight;
    }

    getVisibilityStyles = (bool, scrolling) => {
        if (scrolling) return {opacity: 0.5, visibility: 'visible' };
        return bool ? {opacity: 0, visibility: 'hidden' } : {opacity: 1, visibility: 'visible'};
    };

    get scrollUpElement() {
        const { scrolling } = this.state;
        const canScrollUp = this.canScrollUp;
        const styles = this.getVisibilityStyles(!canScrollUp, scrolling);
        return <FaArrowUp className="scroll up" onClick={this.scrollUp} style={{...styles}} />;
    }

    get scrollDownElement() {
        const { scrolling } = this.state;
        const canScrollDown = this.canScrollDown;
        const styles = this.getVisibilityStyles(!canScrollDown, scrolling);
        return <FaArrowDown className="scroll down" onClick={this.scrollDown} style={{...styles}} />;
    }

    get menu() {
        const maxHeight = this.definedMaxHeight ? this.definedMaxHeight + 'px' : '100%';
        const overflow = this.hasOverflow ? 'scroll' : '';

        return (
            <div class="control-menu" ref={this.menuRef} onScroll={this.handleScroll} style={{...this.styles, maxHeight, overflow }}>
                <div className="control-menu-elements">
                    <div className="control-menu-scroll-elements" style={{height: maxHeight, top: this.currentTop }}>
                        <div className="control-menu-relative-overlay">
                            {this.hasOverflow && this.scrollUpElement}
                            {this.hasOverflow && this.scrollDownElement}
                        </div>
                    </div>
                    {this.options}
                </div>
            </div>
        )
    }

    allowOverflow = (target, bool) => {
        const controls = target.closest('div.controlButtons');
        if (controls) controls.style.overflow = bool ? 'visible' : 'hidden';
    }

    get menuButtonText() {
        return '';
    }

    get menuButton() {
        return <div ref={this.menuButtonRef}  onClick={this.toggleMenu} aria-haspopup="true" className="text"  title='Level Selector' {...accessible(this.toggleMenu)}>{this.menuButtonText}</div>
    }
}
